import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { toast } from "sonner";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

interface IDeleteCredentialParams {
	credentialId: number;
}

interface IDeleteCredentialState {
	loading: boolean;
	error: string | null;
}

const initialState: IDeleteCredentialState = {
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
	};
};

const buildUrl = (credentialId: number) => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Credentials/DeleteCredential?credentialId=${credentialId}`;
};

export const deleteCredential = createAsyncThunk<undefined, IDeleteCredentialParams>(
	"passwordManager/deleteCredential",
	async ({ credentialId }, { rejectWithValue, dispatch }) => {
		try {
			await withPasswordManagerRefresh(dispatch, () => axios.delete(buildUrl(credentialId), { headers: getAuthHeaders() }));
			return undefined;
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to delete credential";
			toast.error(message);
			return rejectWithValue(message);
		}
	},
);

const deleteCredentialSlice = createSlice({
	name: "passwordManager/deleteCredential",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(deleteCredential.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteCredential.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(deleteCredential.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to delete credential";
			});
	},
});

export const deleteCredentialReducer = deleteCredentialSlice.reducer;
